import React from 'react';
import moment from 'moment';
import FormattedDate from 'components/shared/FormattedDate';
import { Icon } from 'ui';
import IncorrectAttemptPopover from './IncorrectAttemptPopover';

class PreviousAttemptWithResponses extends React.Component {
  state = {
    title: this.props.attempt.title,
    createdAt: this.props.attempt.createdAt,
    practiceAttemptResponses: this.props.attempt.practiceAttemptResponses,
  };

  /**
   * This takes the practice attempt responses in state and returns them in
   * order of when they were created (selected by user)
   *
   * @return {Array}
   */
  orderedCorrectResponses = () => {
    return this.state.practiceAttemptResponses
      .filter(response => response.practiceResponseCorrect)
      .sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt)));
  };

  /**
   * This takes the array of incorrect responses and removes any
   * duplicate responses based on the practiceResponseId. It then
   * renders the inside of the popup. We do not show the explanation
   * if it is empty. When there are duplicates we list how may times
   * the user selected that same response
   *
   * @param {Array} incorrectResponses
   * @return {String}
   */
  uniqueIncorrectResponsePopup = incorrectResponses => {
    const uniqueIncorrectResponses = incorrectResponses.filter(
      (obj, pos, arr) => {
        return (
          arr
            .map(mapObj => mapObj.practiceResponseId)
            .indexOf(obj.practiceResponseId) === pos
        );
      },
    );

    return (
      <div>
        {uniqueIncorrectResponses.map(incorrectResponse => {
          const {
            practiceResponseId,
            practiceResponseText,
            practiceResponseExplanation,
          } = incorrectResponse;
          const responseCount = incorrectResponses.filter(
            response => response.practiceResponseId === practiceResponseId,
          ).length;
          return (
            <div
              key={practiceResponseId}
              className="OnDemandPracticeConversation-popupResponse"
            >
              <p>{practiceResponseText}</p>
              {practiceResponseExplanation.length > 0 && (
                <div>
                  <strong>
                    {t('practice.on_demand_practice.conversation.explanation')}
                  </strong>
                  <p>{practiceResponseExplanation}</p>
                </div>
              )}
              {responseCount > 1 && (
                <p>
                  <em>
                    {t(
                      'practice.on_demand_practice.conversation.attempts_count',
                      {
                        count: responseCount,
                      },
                    )}
                  </em>
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * This takes the interaction ID and renders the TD for incorrect responses.
   * If there are no incorrect responses it shows a green check.
   * If there are incorrect responses it shows the count of incorrect responses
   * with a clickable popup that list the incorrect response attempts, optional
   * explanition, and dupclicate count.
   *
   * @param {Integer} interactionId
   * @return {String}
   */
  incorrectResponsesForInteractionId = interactionId => {
    const incorrectResponses = this.state.practiceAttemptResponses.filter(
      response =>
        response.practiceInteractionId === interactionId &&
        response.practiceResponseCorrect === false,
    );
    const incorrectCount = incorrectResponses.length;
    const incorrectCountText = t(
      'practice.on_demand_practice.conversation.incorrect_attempt',
      {
        count: incorrectCount,
      },
    );

    if (incorrectCount > 0) {
      return (
        <td>
          <div className="d-ib OnDemandPracticeConversation-incorrect">
            <IncorrectAttemptPopover
              incorrectCountText={incorrectCountText}
              incorrectResponseContent={this.uniqueIncorrectResponsePopup(
                incorrectResponses,
              )}
            />
          </div>
        </td>
      );
    }
    // If there are no incorrect responses we return the green check
    return (
      <td>
        <p className="w-110 d-ib">
          <span className="Answer--correct">
            <Icon name="correct" className="fz-18" />
          </span>
        </p>
      </td>
    );
  };

  render() {
    const { title, createdAt } = this.state;
    const { hideExplanations } = this.props;

    return (
      <div className="PreviousAttemptWithResponses">
        <h4 className="ff-ss fw-b fz-16 pt-8">{title}</h4>
        <FormattedDate omitCurrentYear className="h-36 lh-36">
          {createdAt}
        </FormattedDate>
        <table className="DataTable Table tablesaw-stack bg-white mt-16 mb-24 OnDemandPracticeConversation-previousResponseTable">
          <thead>
            <tr className="bg-white">
              <th className="DataTable-name w-40p pl-24 pt-16">
                {t(
                  'practice.on_demand_practice.conversation.customer_interaction_header',
                )}
              </th>
              <th className="DataTable-name w-40p pl-24 pt-16">
                {t(
                  'practice.on_demand_practice.conversation.correct_response_header',
                )}
              </th>
              <th className="w-20p">
                {t('practice.on_demand_practice.conversation.attempts_header')}
              </th>
            </tr>
          </thead>
          <tbody>
            {this.orderedCorrectResponses().map(correctResponse => (
              <tr key={correctResponse.id}>
                <td className="DataTable-name va-t pl-24 pt-16">
                  {correctResponse.interactionSubject}
                </td>
                <td className="DataTable-name va-t pl-24 pt-16">
                  <p>{correctResponse.practiceResponseText}</p>
                  {!hideExplanations && (
                    <>
                      <div>
                        <strong>
                          {t(
                            'practice.on_demand_practice.conversation.explanation',
                          )}
                        </strong>
                      </div>
                      <p>
                        {correctResponse.practiceResponseExplanation ? (
                          correctResponse.practiceResponseExplanation
                        ) : (
                          <span className="fs-i">
                            {t(
                              'practice.on_demand_practice.conversation.no_explanation_provided',
                            )}
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </td>
                {this.incorrectResponsesForInteractionId(
                  correctResponse.practiceInteractionId,
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

export default PreviousAttemptWithResponses;
